/**
 * Copyright (c) 2021-2024 Kognitos, Inc. All rights reserved.
 */
import { S3PresignedUrl } from '../generated/graphql';

export enum FileType {
  IMG = 'IMG',
  CSV = 'CSV',
  TXT = 'TXT',
  JSON = 'JSON',
  YML = 'YML',
  PDF = 'PDF',
  EXCEL = 'EXCEL',
  DOCX = 'DOCX',
  HTML = 'HTML',
  EML = 'EML'
}

export const FILE_TYPES_THAT_NEED_DECODING: FileType[] = [
  FileType.CSV,
  FileType.JSON,
  FileType.YML,
  FileType.TXT,
  FileType.HTML
];

// TODO: Use this interface wherever s3Parts are used
export interface IS3Parts {
  bucket: string;
  key: string;
  filename: string;
}

/**
 * Get  the file type, given the content type (`cType`) and the presigned S3 URL (`presignedURL`).
 */
export const getFileType = (
  presignedUrl: S3PresignedUrl | null,
  cType?: string
): FileType | undefined => {
  // Handle cases when `cType` is undefined
  if (cType === undefined || cType === 'application/octet') {
    if (presignedUrl?.s3Url && presignedUrl?.s3Url.endsWith('.pdf'))
      return FileType.PDF;
    return undefined;
  }

  if (cType.startsWith('image/')) return FileType.IMG;

  if (cType === 'text/csv') return FileType.CSV;

  if (cType === 'text/plain') return FileType.TXT;

  if (cType.includes('application/json')) return FileType.JSON;

  if (
    (cType.startsWith('application') || cType.startsWith('text')) &&
    (cType.endsWith('yml') || cType.endsWith('yaml'))
  )
    return FileType.YML;
  if (cType === 'application/pdf') return FileType.PDF;

  if (
    [
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'application/vnd.ms-excel'
    ].includes(cType)
  )
    return FileType.EXCEL;

  if (
    [
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
    ].includes(cType)
  )
    return FileType.DOCX;

  if (cType === 'text/html') return FileType.HTML;
  if (cType === 'message/rfc822') return FileType.EML;
  return undefined;
};
